import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiArrowLeft, FiTag, FiPlay, FiCheckCircle, FiXCircle, FiBookOpen, FiActivity } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const Simulator = ({ ticket, onBack, onResolve }) => {
  const [currentStepId, setCurrentStepId] = useState('step1');
  const [history, setHistory] = useState([]);
  const [isFinished, setIsFinished] = useState(false);
  const [totalScore, setTotalScore] = useState(0);

  const currentStep = ticket.simulation.steps.find(s => s.id === currentStepId);

  const handleOptionClick = (option) => {
    const newHistory = [...history, { ...option, question: currentStep.question }];
    setHistory(newHistory);
    setTotalScore(prev => prev + option.points);
    if (option.nextStep === 'success' || (option.nextStep && option.nextStep.startsWith('fail'))) {
      setIsFinished(true);
    } else if (option.nextStep) {
      setCurrentStepId(option.nextStep);
    }
  };

  if (isFinished) {
    const wasSuccessful = !history.some(h => h.nextStep && h.nextStep.startsWith('fail'));
    return (
      <div className="min-h-full bg-slate-50 flex items-center justify-center p-4 md:p-8">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden" >
          <div className={`p-6 md:p-8 text-center ${wasSuccessful ? 'bg-emerald-600' : 'bg-red-600'} text-white`}>
            <SafeIcon icon={wasSuccessful ? FiCheckCircle : FiXCircle} className="text-5xl mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-black mb-2">{wasSuccessful ? 'Success' : 'Failed'}</h2>
            <p className="text-white/80 text-sm">{ticket.id} Resolution Recap</p>
          </div>
          <div className="p-6 md:p-8 space-y-6">
            <div className="space-y-4">
              {history.map((step, i) => (
                <div key={i} className="flex space-x-4">
                  <div className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-black ${step.correct ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                    {i + 1}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{step.question}</p>
                    <p className="text-sm font-bold text-slate-800">{step.text}</p>
                    <p className={`text-xs italic ${step.correct ? 'text-emerald-600' : 'text-red-500'}`}>{step.feedback}</p>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => onResolve(ticket.id, wasSuccessful)} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl">
              Finalize Resolution
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-4 md:px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center space-x-3">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"><SafeIcon icon={FiArrowLeft} /></button>
          <div className="min-w-0">
            <h2 className="text-sm md:text-lg font-black text-slate-800 truncate">{ticket.id}</h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest truncate">{ticket.title}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Current Score</div>
          <div className="text-lg font-black text-blue-600 leading-none">{totalScore}</div>
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Mobile View: Scrollable Context and Workspace */}
        <div className="flex-1 overflow-y-auto p-4 md:p-12">
          <div className="max-w-2xl mx-auto space-y-8">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm md:hidden">
               <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Incident Summary</h3>
               <p className="text-sm text-slate-600 italic">"{ticket.description}"</p>
            </div>

            <div className="text-center space-y-3">
              <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                <SafeIcon icon={FiPlay} /> <span>Simulation Active</span>
              </div>
              <h2 className="text-xl md:text-3xl font-black text-slate-800 leading-tight">{currentStep?.question}</h2>
            </div>

            <div className="space-y-3">
              {currentStep?.options.map(option => (
                <button key={option.id} onClick={() => handleOptionClick(option)} className="w-full text-left p-4 md:p-6 rounded-2xl bg-white border-2 border-slate-200 hover:border-blue-400 hover:shadow-xl transition-all group">
                  <div className="flex items-center justify-between">
                    <span className="text-sm md:text-base font-bold text-slate-700 group-hover:text-blue-600">{option.text}</span>
                    <div className="w-5 h-5 rounded-full border-2 border-slate-200 shrink-0 ml-4 group-hover:border-blue-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar Context (Desktop Only) */}
        <div className="hidden md:block w-80 bg-white border-l border-slate-200 p-8 overflow-y-auto space-y-8">
           <section>
             <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Requester</h3>
             <div className="flex items-center space-x-3">
               <img src={ticket.avatar} className="w-10 h-10 rounded-xl" alt="" />
               <div>
                 <div className="text-sm font-bold text-slate-800">{ticket.user}</div>
                 <div className="text-xs text-slate-500">{ticket.department}</div>
               </div>
             </div>
           </section>
           <section className="bg-slate-50 p-4 rounded-xl border border-slate-100 italic text-sm text-slate-600">
             "{ticket.description}"
           </section>
           <section className="space-y-4">
              <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">System Info</h3>
              <div className="bg-slate-900 rounded-xl p-4 font-mono text-[10px] text-blue-400 space-y-1">
                <p>IP: {ticket.systemInfo.ip}</p>
                <p>OS: {ticket.systemInfo.os}</p>
                <p>HOST: {ticket.systemInfo.device}</p>
              </div>
           </section>
        </div>
      </div>
    </div>
  );
};

export default Simulator;